import App from 'antd/lib/app';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from '../../../components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from '../../../components/sidebar-filters/useSavedFiltersList';
import { DEFAULT_PAGINATION } from '../../../domain/commonConst';
import { useSavedFilterCreateMutation, useSavedFilterDeleteMutation, useSavedFilterUpdateMutation } from '../../../domain/saved-filters/queries';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { usePageLocation } from '../../usePageState';
import {
    checkFilterInvalidValues,
    countNotEmptyFilters,
    transformEventPerformanceSavedFilterValuesFromDto,
    transformEventPerformanceSavedFilterValuesToDto,
} from './utils';

export interface IEventPerformancePageState {
    // Filters
    filter?: {
        date_range_preset?: string;
        event_duration_preset?: string;
        program_name?: number | number[];
        nomination_preset?: string;
    };

    // Used by whole page
    companyId?: number;

    pagination?: {
        current: number;
        pageSize: number;
    };

    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export interface IEventPerformanceSidebarFilters {
    date_range_preset?: string;
    event_duration_preset?: string;
    program_name?: number[];
    nomination_preset?: string;
}

export interface IEventPerformanceSidebarFiltersContext {
    filtersCount: number;
    filters: IEventPerformanceSidebarFilters;
    savedFilters: ISavedFilter[];

    isSavedFiltersLoading: boolean;

    handleFilterChange: (key: string, value: any) => void;
    handleFiltersClear: () => void;
    handleDefaultFilters: (filters: IEventPerformanceSidebarFilters) => void;

    selectedSavedFilter: ICreateSavedFilter | null;
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;

    appliedSavedFilter: ISavedFilter | null; // Using for applying filters
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const EventPerformanceSidebarFiltersContext = React.createContext<IEventPerformanceSidebarFiltersContext>(
    {} as IEventPerformanceSidebarFiltersContext
);

export const convertEventPerformancePageStateToFilterState = (pageState: IEventPerformancePageState): IEventPerformanceSidebarFilters => {
    const result: IEventPerformanceSidebarFilters = {};
    const filterData = pageState.filter || {};

    // Convert program
    if (filterData.program_name !== undefined) {
        result.program_name = Array.isArray(filterData.program_name) ? filterData.program_name : [filterData.program_name];
    }

    if (filterData.date_range_preset !== undefined) {
        result.date_range_preset = filterData.date_range_preset;
    }

    if (filterData.event_duration_preset !== undefined) {
        result.event_duration_preset = filterData.event_duration_preset;
    }

    if (filterData.nomination_preset !== undefined) {
        result.nomination_preset = filterData.nomination_preset;
    }

    return result;
};

export const EventPerformanceSidebarFiltersProvider = ({ children }: { children: React.ReactNode }) => {
    const { filtersCount, filters, handleFilterChange, handleFiltersChange, handleFiltersClear } = useSidebarFiltering();

    // Saving Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    } = useEventPerformanceSavedFilters();

    const onFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when filters change
        },
        [handleApplySavedFilter, handleFilterChange]
    );

    const onDefaultFiltersApply = useCallback(
        (filters: IEventPerformanceSidebarFilters) => {
            handleFiltersChange(filters);
        },
        [handleFiltersChange]
    );

    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleFiltersChange]
    );

    const onFiltersClear = useCallback(() => {
        handleFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleFiltersClear]);

    return (
        <EventPerformanceSidebarFiltersContext.Provider
            value={{
                filtersCount,
                filters,
                savedFilters,

                isSavedFiltersLoading,

                handleFilterChange: onFiltersChange,
                handleFiltersClear: onFiltersClear,
                handleDefaultFilters: onDefaultFiltersApply,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </EventPerformanceSidebarFiltersContext.Provider>
    );
};

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: IEventPerformancePageState = queryToState(location.search);
    const [filters, setFilters] = useState<IEventPerformanceSidebarFilters>(convertEventPerformancePageStateToFilterState(pageState));
    const filtersCount = useMemo(() => countNotEmptyFilters(filters), [filters]);

    const handleFilterChange = (key: string, value: unknown) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // Update the URL query parameters with filter object structure
        const updatedPageState = {
            ...pageState,
            filter: {
                ...pageState.filter,
                [key]: value,
            },
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        };

        setPageQuery(updatedPageState);
    };

    const handleFiltersChange = (newFilters: IEventPerformanceSidebarFilters) => {
        setFilters(newFilters);
        setPageQuery({
            ...pageState,
            filter: newFilters,
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        });
    };

    const handleFiltersClear = () => {
        setFilters({});

        setPageQuery({
            ...pageState,
            filter: {},
        });
    };

    return {
        filtersCount,
        filters,

        handleFilterChange,
        handleFiltersChange,
        handleFiltersClear,
    };
};

const useEventPerformanceSavedFilters = () => {
    const { notification } = App.useApp();
    const context = useSidebarFilterOptionsContext();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.eventAnalytics);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            // Transform filter values from DTO format to Interface
            filter.filter_values = transformEventPerformanceSavedFilterValuesFromDto(filter.filter_values);

            const outdatedFields = checkFilterInvalidValues(filter.filter_values, context);
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [context]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map((filter: any) => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformEventPerformanceSavedFilterValuesToDto(filterData.filter_values);

            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformEventPerformanceSavedFilterValuesToDto(filterData.filter_values);

            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,
        validateSavedFilter,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    };
};

export const useEventPerformanceSidebarFiltersContext = () => {
    const context = React.useContext(EventPerformanceSidebarFiltersContext);
    if (!context) {
        throw new Error('useEventPerformanceSidebarFiltersContext must be used within EventPerformanceSidebarFiltersContext');
    }

    return context;
};
